/**
 * People-attribution S10 (D-M): the shareable page form of the version row's
 * «Sources» popup — `/project/:projectId/reports/funnel/:onboardingId/:versionId/sources`.
 * Same view, same served method; the URL carries window, unit, alias and tab
 * so a reload or a pasted link reproduces the view. «‹ Back to Reports»
 * returns to the versions table with the same scope.
 */
import React from 'react';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { FunnelInsightsApiClient } from '../api/insightsClient';
import { FUNNEL_REPORTS_TEST_IDS } from '../testIds';
import { applyFunnelUnitParam, parseFunnelUnitParam, type FunnelUnit } from '../funnelUnit';
import { parseTrafficCampaignParam, parseTrafficSourcesParam, TRAFFIC_CAMPAIGN_URL_PARAM, TRAFFIC_SOURCES_URL_PARAM, type TrafficSourcesView } from '../trafficSourcesRows';
import { isValidTimeZone, zonedDayBoundIso } from '../timezoneWindow';
import { buildReportsBackPath, FUNNEL_TRAFFIC_SOURCES_NAME_PARAMS } from '../funnelTrafficSourcesRoute';
import { buildRowEconomicsPath, rowServedPrefill } from '../rowServedPrefill';
import type { FunnelTrafficRow } from '../funnelTrafficSourcesRows';
import { useFunnelTrafficSources } from '../hooks/useFunnelTrafficSources';
import { FunnelTrafficSourcesView } from '../components/FunnelTrafficSourcesView';

export interface FunnelTrafficSourcesPageProps {
  /** S12 §15.3: the Creative-Decisions UI flag — the same value Reports receives; gates the watch-only pLTV chip. */
  creativeDecisionsEnabled?: boolean;
}

export function FunnelTrafficSourcesPage({ creativeDecisionsEnabled = false }: FunnelTrafficSourcesPageProps = {}): React.ReactElement {
  const { projectId = '', onboardingId = '', versionId = '' } = useParams<{ projectId: string; onboardingId: string; versionId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const client = React.useMemo(() => new FunnelInsightsApiClient(), []);
  const from = searchParams.get('from') || '';
  const to = searchParams.get('to') || '';
  const tzParam = searchParams.get('tz') || '';
  const tz = isValidTimeZone(tzParam) ? tzParam : 'UTC';
  const alias = searchParams.get('alias') || undefined;
  const unit = parseFunnelUnitParam(searchParams.get('unit'));
  const view = parseTrafficSourcesParam(searchParams.get(TRAFFIC_SOURCES_URL_PARAM)) ?? 'campaigns';
  // S13 wave D: the selected campaign rides the URL next to the tab (filter / highlight, client-side).
  const selectedCampaignId = parseTrafficCampaignParam(searchParams.get(TRAFFIC_CAMPAIGN_URL_PARAM));
  const attributionModelId = searchParams.get('attributionModelId') || undefined;
  const windowValid = Boolean(from && to && Number.isFinite(Date.parse(from)) && Number.isFinite(Date.parse(to)));

  const query = windowValid && projectId && onboardingId && versionId
    ? {
      projectId,
      onboardingId,
      versionId,
      alias,
      from: zonedDayBoundIso(from, tz),
      to: zonedDayBoundIso(to, tz),
      tz: tz !== 'UTC' ? tz : undefined,
      unit,
      attributionModelId,
    }
    : null;
  const result = useFunnelTrafficSources(client, query);

  const updateParams = (mutate: (params: URLSearchParams) => void): void => {
    const next = new URLSearchParams(searchParams);
    mutate(next);
    setSearchParams(next, { replace: true });
  };
  const handleUnitChange = (nextUnit: FunnelUnit): void => updateParams(params => applyFunnelUnitParam(params, nextUnit));
  const handleViewChange = (nextView: TrafficSourcesView): void => updateParams(params => params.set(TRAFFIC_SOURCES_URL_PARAM, nextView));
  // One URL write for both keys; the key is written only for a non-empty value (AC-S13D-006).
  const handleCampaignSelect = (selection: { view: TrafficSourcesView; campaignId?: string }): void => updateParams(params => {
    params.set(TRAFFIC_SOURCES_URL_PARAM, selection.view);
    if (selection.campaignId) params.set(TRAFFIC_CAMPAIGN_URL_PARAM, selection.campaignId);
    else params.delete(TRAFFIC_CAMPAIGN_URL_PARAM);
  });

  // Phase 2-min (AC-038): «Unit economics →» on campaign rows — the mapper
  // decides eligibility and the seeds ride the URL to the hub's ?tab=pricing.
  // The window passed is the SAME zoned-bound window the served figures were
  // fetched for; the Phase-1 pull in the hub runs against this funnel scope.
  const rowEconomicsHref = React.useCallback((row: FunnelTrafficRow): string | undefined => {
    if (!query) return undefined;
    const seeds = rowServedPrefill(row, { from: query.from, to: query.to });
    if (!seeds) return undefined;
    return buildRowEconomicsPath({
      projectId,
      onboardingId,
      versionId,
      from: query.from,
      to: query.to,
      campaignKey: row.campaignId ?? row.id,
      seeds,
    });
  }, [query, projectId, onboardingId, versionId]);

  const backPath = buildReportsBackPath({ projectId, onboardingId, versionId, from, to, tz, alias, unit });
  // Identity layer (S8/S10 review rule): the name leads, the id demotes to the scope chip.
  const versionName = searchParams.get(FUNNEL_TRAFFIC_SOURCES_NAME_PARAMS.versionName) || undefined;
  const onboardingName = searchParams.get(FUNNEL_TRAFFIC_SOURCES_NAME_PARAMS.onboardingName) || undefined;
  // S11 review: a version without a name (identity carries none) still leads
  // with the onboarding name; the raw id demotes to the scope chip.
  const versionLabel = versionName
    ? `${versionName}${onboardingName ? ` — ${onboardingName}` : ''}`
    : onboardingName ?? versionId;

  return (
    <Box sx={{ p: 2, maxWidth: 1400, mx: 'auto' }} data-testid={FUNNEL_REPORTS_TEST_IDS.trafficIntoFunnelPage}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap flexWrap="wrap">
            <Button
              component={RouterLink}
              to={backPath}
              size="small"
              startIcon={<ArrowBackIcon fontSize="small" />}
              data-testid={FUNNEL_REPORTS_TEST_IDS.trafficIntoFunnelBack}
            >
              Back to Reports
            </Button>
            <Typography variant="h6" component="h1">
              {`Traffic into ${versionLabel}`}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            This page is the shareable form of the version row&apos;s «Sources» popup — same numbers, same service.
          </Typography>
          {!windowValid ? (
            <Alert severity="warning">
              This link carries no valid window. Open the version row&apos;s «Sources» in Reports and use «Open as page» — the window, unit and tab ride the URL.
            </Alert>
          ) : (
            <FunnelTrafficSourcesView
              state={result.state}
              data={result.data}
              error={result.error}
              unit={unit}
              onUnitChange={handleUnitChange}
              view={view}
              onViewChange={handleViewChange}
              selectedCampaignId={selectedCampaignId}
              onCampaignSelect={handleCampaignSelect}
              projectId={projectId}
              watchOnlyPltvEnabled={creativeDecisionsEnabled}
              rowEconomicsHref={rowEconomicsHref}
              zonesWindow={query ? { from: query.from, to: query.to } : undefined}
              scopeChips={[
                `${alias ? `${alias} · ` : ''}${versionId}`,
                `${from.slice(0, 10)} → ${to.slice(0, 10)}${tz !== 'UTC' ? ` · ${tz}` : ''}`,
              ]}
            />
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
